import { z } from 'zod';

/**
 * Domain schemas for event log entities
 * Using Base+Variants pattern for scalability following @docs/schemas-plan.md
 * Schema-first approach replacing legacy interfaces
 */

/**
 * Base schema for event logs
 * Contains all possible fields following Base+Variants pattern
 */
const BaseEventLogSchema = z.object({
  id: z.string(),
  userId: z
    .union([z.string(), z.any()])
    .transform(val =>
      typeof val === 'object' && val.toString ? val.toString() : String(val)
    ),
  message: z
    .string()
    .min(1, 'Message is required')
    .max(500, 'Message too long'),
  data: z.record(z.string(), z.unknown()).optional(),
  source: z.string().max(100, 'Source too long').optional(),
  createdAt: z
    .union([z.date(), z.iso.datetime()])
    .transform(val => (val instanceof Date ? val.toISOString() : val)),
  updatedAt: z
    .union([z.date(), z.iso.datetime()])
    .transform(val => (val instanceof Date ? val.toISOString() : val)),
});

/**
 * Event log schema variants for different contexts
 * Following Base+Variants pattern from @docs/schemas-plan.md
 */
export const EventLogSchemas = {
  // Full event log data (internal use, all fields)
  full: BaseEventLogSchema,

  // Event log for creation requests (API routes, no server-added fields)
  create: BaseEventLogSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  }),

  // Event log for updates (partial fields except id)
  update: BaseEventLogSchema.partial().extend({
    id: z.string(), // Keep id required
  }),

  // Event log for listing (optimized for performance, no data payload)
  list: BaseEventLogSchema.omit({
    data: true, // Exclude potentially large data field for list views
  }),
};

// Export types using z.infer<> following schema-first approach
export type EventLog = z.infer<typeof EventLogSchemas.full>;
export type CreateEventLog = z.infer<typeof EventLogSchemas.create>;
export type UpdateEventLog = z.infer<typeof EventLogSchemas.update>;
export type EventLogListItem = z.infer<typeof EventLogSchemas.list>;
